/**
 * AI Service
 * Handles AI chat (Support AI + AI Coach), Q&A training database, and query logging
 * Uses either Google Gemini (via Firebase) or OpenAI-compatible API based on settings
 */

import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  addDoc,
  getDocs,
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { buildPlatformGuideForAI, answerHowToLocally } from '@/lib/platform-guide';

// ============ PLATFORM SEARCH TYPES ============

export interface PlatformSearchResult {
  type: 'artist' | 'track' | 'release';
  id: string;
  title: string;
  subtitle?: string;   // artist name for tracks/releases, genre for artists
  imageUrl?: string;
  link: string;        // internal app link
}

// ============ TYPES ============

export interface AISettings {
  provider: 'gemini' | 'openai' | 'anthropic' | 'openrouter';
  apiKey: string;
  model: string;
  systemPrompt?: string;
  coachSystemPrompt?: string;
  maxTokens?: number;
  temperature?: number;
}

export interface QAEntry {
  id: string;
  question: string;
  answer: string;
  category: string;
  tags?: string[];
  createdAt?: any;
  updatedAt?: any;
  createdBy?: string;
  isActive: boolean;
}

export interface AIQueryLog {
  id: string;
  userId: string;
  userName?: string;
  question: string;
  response: string;
  context: 'support' | 'coach';
  matchedQAId?: string;  // if matched from Q&A database
  wasHelpful?: boolean;
  createdAt?: any;
}

// ============ AI SETTINGS ============

const AI_SETTINGS_DOC = 'settings/aiSettings';

const DEPRECATED_GEMINI_MODELS: Record<string, string> = {
  'gemini-pro':          'gemini-2.5-flash',
  'gemini-pro-vision':   'gemini-2.5-flash',
  'gemini-ultra':        'gemini-2.5-flash',
  'gemini-1.0-pro':      'gemini-2.5-flash',
  'gemini-1.0-pro-001':  'gemini-2.5-flash',
  'gemini-1.5-flash':    'gemini-2.5-flash',
  'gemini-1.5-flash-002':'gemini-2.5-flash',
  'gemini-1.5-pro':      'gemini-2.5-flash',
  'gemini-1.5-pro-002':  'gemini-2.5-flash',
  'gemini-2.0-flash':    'gemini-2.5-flash',
  'gemini-2.0-flash-001':'gemini-2.5-flash',
};

export async function getAISettings(): Promise<AISettings | null> {
  try {
    const snap = await getDoc(doc(db, AI_SETTINGS_DOC));
    if (!snap.exists()) return null;
    const data = snap.data() as AISettings;
    // Auto-upgrade deprecated Gemini model names so the app never 404s
    if (data.model && DEPRECATED_GEMINI_MODELS[data.model]) {
      const upgraded = { ...data, model: DEPRECATED_GEMINI_MODELS[data.model] };
      // Persist the upgrade silently so it's fixed for everyone
      saveAISettings(upgraded).catch(() => {});
      return upgraded;
    }
    return data;
  } catch (error) {
    console.error('Error getting AI settings:', error);
    return null;
  }
}

export async function saveAISettings(settings: AISettings): Promise<boolean> {
  try {
    await setDoc(doc(db, AI_SETTINGS_DOC), {
      ...settings,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error saving AI settings:', error);
    return false;
  }
}

// ============ Q&A DATABASE ============

export async function getQAEntries(): Promise<QAEntry[]> {
  try {
    const q = query(collection(db, 'aiQA'), orderBy('category', 'asc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as QAEntry));
  } catch (error) {
    console.error('Error fetching Q&A entries:', error);
    // Fallback without orderBy
    try {
      const snapshot = await getDocs(collection(db, 'aiQA'));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as QAEntry));
    } catch { return []; }
  }
}

export async function createQAEntry(entry: Omit<QAEntry, 'id'>): Promise<string | null> {
  try {
    const docRef = await addDoc(collection(db, 'aiQA'), {
      ...entry,
      createdAt: serverTimestamp(),
      isActive: true,
    });
    return docRef.id;
  } catch (error) {
    console.error('Error creating Q&A entry:', error);
    return null;
  }
}

export async function updateQAEntry(id: string, data: Partial<QAEntry>): Promise<boolean> {
  try {
    const { id: _, ...updateData } = data;
    await updateDoc(doc(db, 'aiQA', id), {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
    return true;
  } catch (error) {
    console.error('Error updating Q&A entry:', error);
    return false;
  }
}

export async function deleteQAEntry(id: string): Promise<boolean> {
  try {
    await deleteDoc(doc(db, 'aiQA', id));
    return true;
  } catch (error) {
    console.error('Error deleting Q&A entry:', error);
    return false;
  }
}

// ============ QUERY LOGGING ============

export async function logAIQuery(logEntry: Omit<AIQueryLog, 'id'>): Promise<string | null> {
  try {
    const docRef = await addDoc(collection(db, 'aiQueryLogs'), {
      ...logEntry,
      createdAt: serverTimestamp(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error logging AI query:', error);
    return null;
  }
}

export async function getAIQueryLogs(options?: {
  context?: 'support' | 'coach';
  limitCount?: number;
}): Promise<AIQueryLog[]> {
  try {
    let q = query(collection(db, 'aiQueryLogs'), orderBy('createdAt', 'desc'));
    if (options?.context) {
      q = query(collection(db, 'aiQueryLogs'), where('context', '==', options.context), orderBy('createdAt', 'desc'));
    }
    if (options?.limitCount) {
      q = query(q, limit(options.limitCount));
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AIQueryLog));
  } catch (error) {
    console.error('Error fetching AI query logs:', error);
    try {
      const snapshot = await getDocs(collection(db, 'aiQueryLogs'));
      return snapshot.docs.map(d => ({ id: d.id, ...d.data() } as AIQueryLog));
    } catch { return []; }
  }
}

export async function markQueryHelpful(logId: string, helpful: boolean): Promise<boolean> {
  try {
    await updateDoc(doc(db, 'aiQueryLogs', logId), { wasHelpful: helpful });
    return true;
  } catch (error) {
    console.error('Error marking query:', error);
    return false;
  }
}

// ============ PLATFORM SEARCH ============

/**
 * Extract meaningful search keywords from a question (words > 2 chars, no common stop words)
 */
function extractKeywords(question: string): string[] {
  const stopWords = new Set(['the', 'and', 'for', 'are', 'but', 'not', 'you', 'all', 'can',
    'had', 'her', 'was', 'one', 'our', 'out', 'day', 'get', 'has', 'him', 'his', 'how',
    'its', 'may', 'new', 'now', 'old', 'see', 'two', 'way', 'who', 'boy', 'did', 'let',
    'put', 'say', 'she', 'too', 'use', 'this', 'that', 'with', 'have', 'from', 'what',
    'they', 'will', 'been', 'come', 'does', 'into', 'just', 'like', 'make', 'more',
    'more', 'over', 'show', 'some', 'than', 'them', 'then', 'there', 'time', 'very',
    'when', 'where', 'which', 'while', 'your', 'about', 'could', 'first', 'music',
    'song', 'track', 'play', 'find', 'search', 'look', 'tell', 'know']);
  return question.toLowerCase()
    .split(/\s+/)
    .map(w => w.replace(/[^a-z0-9]/g, ''))
    .filter(w => w.length > 2 && !stopWords.has(w));
}

/**
 * Score a text field against query keywords (0 to 1)
 */
function scoreMatch(text: string, keywords: string[]): number {
  if (!text || keywords.length === 0) return 0;
  const lower = text.toLowerCase();
  let hits = 0;
  for (const kw of keywords) {
    if (lower.includes(kw)) hits++;
  }
  return hits / keywords.length;
}

/**
 * Search artists, works, and releases in Firestore based on a natural-language question.
 * Returns top matches as PlatformSearchResult[].
 */
async function searchPlatformContent(question: string): Promise<PlatformSearchResult[]> {
  const keywords = extractKeywords(question);
  if (keywords.length === 0) return [];

  const results: Array<{ score: number; result: PlatformSearchResult }> = [];

  try {
    // --- Artists ---
    const artistsSnap = await getDocs(query(collection(db, 'artists'), limit(100)));
    for (const d of artistsSnap.docs) {
      const data = d.data();
      if (data.adminHidden) continue;
      const name = data.name || '';
      const bio = data.bio || '';
      const genres = (data.genres || []).join(' ');
      const score = Math.max(
        scoreMatch(name, keywords) * 1.5,   // name match weighted higher
        scoreMatch(bio, keywords) * 0.5,
        scoreMatch(genres, keywords) * 0.7
      );
      if (score > 0.15) {
        results.push({
          score,
          result: {
            type: 'artist',
            id: d.id,
            title: name,
            subtitle: (data.genres || []).slice(0, 2).join(', ') || 'Artist',
            imageUrl: data.imageUrl || '',
            link: `/artists/${data.slug || d.id}`,
          },
        });
      }
    }

    // --- Works / Tracks ---
    const worksSnap = await getDocs(query(collection(db, 'works'), limit(150)));
    for (const d of worksSnap.docs) {
      const data = d.data();
      if (data.privacy === 'private' || data.status === 'Draft') continue;
      const title = data.title || '';
      const artist = data.artist || '';
      const genre = data.genre || '';
      const score = Math.max(
        scoreMatch(title, keywords) * 1.5,
        scoreMatch(artist, keywords) * 1.0,
        scoreMatch(genre, keywords) * 0.5
      );
      if (score > 0.2) {
        results.push({
          score,
          result: {
            type: 'track',
            id: d.id,
            title,
            subtitle: artist || 'Unknown Artist',
            imageUrl: data.coverArtUrl || data.imageUrl || '',
            link: `/music/${d.id}`,
          },
        });
      }
    }

    // --- Releases ---
    const releasesSnap = await getDocs(query(collection(db, 'releases'), limit(100)));
    for (const d of releasesSnap.docs) {
      const data = d.data();
      const title = data.title || '';
      const artistName = (typeof data.artist === 'string' ? data.artist : data.artist?.name) || '';
      const score = Math.max(
        scoreMatch(title, keywords) * 1.5,
        scoreMatch(artistName, keywords) * 1.0
      );
      if (score > 0.2) {
        results.push({
          score,
          result: {
            type: 'release',
            id: d.id,
            title,
            subtitle: `${data.releaseType || 'Release'} by ${artistName}`,
            imageUrl: data.coverArtUrl || '',
            link: `/music/${d.id}`,
          },
        });
      }
    }
  } catch (err) {
    console.error('Platform search error:', err);
  }

  // Sort by score descending, deduplicate by title, return top 6
  results.sort((a, b) => b.score - a.score);
  const seen = new Set<string>();
  const deduplicated: PlatformSearchResult[] = [];
  for (const { result } of results) {
    const key = result.title.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      deduplicated.push(result);
    }
    if (deduplicated.length >= 6) break;
  }
  return deduplicated;
}

/**
 * Support AI with platform awareness — searches Firestore for relevant content,
 * injects it as RAG context, and returns both the AI text answer and structured result cards.
 */
export async function askSupportAI(
  question: string,
  userId: string,
  userName?: string
): Promise<{ answer: string; results: PlatformSearchResult[]; source: 'qa' | 'ai' | 'fallback' }> {
  // 1. Check Q&A database first (static answers for common questions)
  const qaMatch = await searchQA(question);

  // 2. Search platform content in parallel
  const platformResults = await searchPlatformContent(question);

  if (qaMatch) {
    // Still log and return the QA match with platform results appended
    await logAIQuery({ userId, userName, question, response: qaMatch.answer, context: 'support', matchedQAId: qaMatch.id });
    return { answer: qaMatch.answer, results: platformResults, source: 'qa' };
  }

  // How-to knowledge: what BB can do and where things live. Built per-question
  // so the AI can teach users to DO and FIND things, not just answer about content.
  const guideContext = buildPlatformGuideForAI(question);

  // 3. Get AI settings
  const settings = await getAISettings();
  if (!settings?.apiKey) {
    // No AI key configured — still answer "how do I…" from the guide directly.
    const local = answerHowToLocally(question);
    const fallback = local
      ? local
      : platformResults.length > 0
        ? `I found ${platformResults.length} result${platformResults.length !== 1 ? 's' : ''} on the platform that might help. Check the cards below.`
        : "I couldn't find that. Tell me what you're trying to do (e.g. \"how do I publish a track?\" or \"where's my wallet?\") and I'll walk you through it.";
    await logAIQuery({ userId, userName, question, response: fallback, context: 'support' });
    return { answer: fallback, results: platformResults, source: 'fallback' };
  }

  // 4. Build platform-aware system prompt — the in-app guide persona.
  const platformSystemPrompt = `You are the in-app guide and support assistant for Breakin' Bread (BB), a music platform for independent artists ("makers") and fans.
Your #1 job: when someone asks HOW to do something or WHERE to find something, TEACH them. Give clear, short numbered steps and name the exact sidebar item, tab, or button to tap. Be specific and action-oriented, and end how-to answers by pointing to where in the app to go.
Use the PLATFORM GUIDE below as your source of truth for what BB can do, how it works, and where features live. Prefer its steps over guessing. If something isn't in the guide, say you're not certain and suggest the closest thing — never invent buttons, pages, or menu items that may not exist.
For questions about specific artists, songs, releases, or products, use the "Relevant platform content" links provided — never make up links.
BB's platform fee is π% (about 3.14%). Keep answers concise, friendly, and encouraging.`;

  // 5. Build platform context from search results
  let platformContext = '';
  if (platformResults.length > 0) {
    platformContext = `Relevant platform content found:\n` + platformResults.map(r =>
      `- ${r.type.toUpperCase()}: "${r.title}"${r.subtitle ? ` (${r.subtitle})` : ''} → ${r.link}`
    ).join('\n');
  }

  // 6. Build Q&A context
  let qaContext = '';
  try {
    const allQA = await getQAEntries();
    const relevant = allQA.filter(e => e.isActive).slice(0, 8).map(e => `Q: ${e.question}\nA: ${e.answer}`).join('\n\n');
    if (relevant) qaContext = `Curated Q&A:\n${relevant}`;
  } catch { /* ignore */ }

  const fullContext = [`PLATFORM GUIDE:\n${guideContext}`, qaContext, platformContext].filter(Boolean).join('\n\n');

  // 7. Call AI API
  try {
    const answer = await callAIAPI(settings, platformSystemPrompt, question, fullContext);
    await logAIQuery({ userId, userName, question, response: answer, context: 'support' });
    return { answer, results: platformResults, source: 'ai' };
  } catch (error: any) {
    console.error('Support AI error:', error);
    // On AI failure, fall back to the local step-by-step guide when we can.
    const local = answerHowToLocally(question);
    const fallback = local
      ? local
      : platformResults.length > 0
        ? `Here's some relevant content below — my full answer hit an error, please try again.`
        : `I hit an error: ${error.message}. Please try again.`;
    await logAIQuery({ userId, userName, question, response: fallback, context: 'support' });
    return { answer: fallback, results: platformResults, source: 'fallback' };
  }
}

// ============ AI CHAT ============

/**
 * Search Q&A database for a matching answer first
 */
async function searchQA(question: string): Promise<QAEntry | null> {
  try {
    const entries = await getQAEntries();
    const questionLower = question.toLowerCase();

    // Simple keyword matching — find best match
    let bestMatch: QAEntry | null = null;
    let bestScore = 0;

    for (const entry of entries) {
      if (!entry.isActive) continue;
      const entryQuestion = entry.question.toLowerCase();
      const entryTags = (entry.tags || []).map(t => t.toLowerCase());

      // Exact match
      if (entryQuestion === questionLower) return entry;

      // Calculate word overlap score
      const questionWords = questionLower.split(/\s+/).filter(w => w.length > 2);
      const entryWords = entryQuestion.split(/\s+/).filter(w => w.length > 2);
      const allEntryWords = [...entryWords, ...entryTags];

      let score = 0;
      for (const word of questionWords) {
        if (allEntryWords.some(ew => ew.includes(word) || word.includes(ew))) {
          score++;
        }
      }

      const normalizedScore = questionWords.length > 0 ? score / questionWords.length : 0;
      if (normalizedScore > bestScore && normalizedScore >= 0.5) {
        bestScore = normalizedScore;
        bestMatch = entry;
      }
    }

    return bestMatch;
  } catch {
    return null;
  }
}

/**
 * Call AI API (OpenAI-compatible format)
 */
async function callAIAPI(
  settings: AISettings,
  systemPrompt: string,
  userMessage: string,
  qaContext?: string
): Promise<string> {
  const messages = [
    { role: 'system', content: systemPrompt + (qaContext ? `\n\nRelevant Q&A context:\n${qaContext}` : '') },
    { role: 'user', content: userMessage },
  ];

  let endpoint = '';
  let headers: Record<string, string> = { 'Content-Type': 'application/json' };
  let body: any = {};

  if (settings.provider === 'openai') {
    endpoint = 'https://api.openai.com/v1/chat/completions';
    headers['Authorization'] = `Bearer ${settings.apiKey}`;
    body = {
      model: settings.model || 'gpt-4o-mini',
      messages,
      max_tokens: settings.maxTokens || 1024,
      temperature: settings.temperature ?? 0.7,
    };
  } else if (settings.provider === 'anthropic') {
    endpoint = 'https://api.anthropic.com/v1/messages';
    headers['x-api-key'] = settings.apiKey;
    headers['anthropic-version'] = '2023-06-01';
    headers['anthropic-dangerous-direct-browser-access'] = 'true';
    body = {
      model: settings.model || 'claude-sonnet-4-20250514',
      max_tokens: settings.maxTokens || 1024,
      system: systemPrompt + (qaContext ? `\n\nRelevant Q&A context:\n${qaContext}` : ''),
      messages: [{ role: 'user', content: userMessage }],
    };
  } else {
    // Gemini via Google AI Studio REST
    // Map deprecated/removed model names to their current replacements
    const GEMINI_MODEL_UPGRADES: Record<string, string> = {
      'gemini-pro':          'gemini-2.5-flash',
      'gemini-pro-vision':   'gemini-2.5-flash',
      'gemini-ultra':        'gemini-2.5-flash',
      'gemini-1.0-pro':      'gemini-2.5-flash',
      'gemini-1.0-pro-001':  'gemini-2.5-flash',
      'gemini-1.5-flash':    'gemini-2.5-flash',
      'gemini-1.5-flash-002':'gemini-2.5-flash',
      'gemini-1.5-pro':      'gemini-2.5-flash',
      'gemini-1.5-pro-002':  'gemini-2.5-flash',
      'gemini-2.0-flash':    'gemini-2.5-flash',
      'gemini-2.0-flash-001':'gemini-2.5-flash',
    };
    const rawModel = settings.model || 'gemini-2.5-flash';
    const geminiModel = GEMINI_MODEL_UPGRADES[rawModel] ?? rawModel;
    endpoint = `https://generativelanguage.googleapis.com/v1beta/models/${geminiModel}:generateContent?key=${settings.apiKey}`;
    body = {
      contents: [{ parts: [{ text: `${systemPrompt}\n\n${qaContext ? `Context:\n${qaContext}\n\n` : ''}User: ${userMessage}` }] }],
      generationConfig: {
        maxOutputTokens: settings.maxTokens || 1024,
        temperature: settings.temperature ?? 0.7,
      },
    };
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(`AI API error (${response.status}): ${error}`);
  }

  const data = await response.json();

  if (settings.provider === 'openai') {
    return data.choices?.[0]?.message?.content || 'No response generated.';
  } else if (settings.provider === 'anthropic') {
    return data.content?.[0]?.text || 'No response generated.';
  } else {
    return data.candidates?.[0]?.content?.parts?.[0]?.text || 'No response generated.';
  }
}

/**
 * Main AI chat function — checks Q&A database first, then calls API
 */
export async function askAI(
  question: string,
  context: 'support' | 'coach',
  userId: string,
  userName?: string
): Promise<{ answer: string; source: 'qa' | 'ai' | 'fallback'; matchedQAId?: string }> {
  // 1. Check Q&A database first
  const qaMatch = await searchQA(question);
  if (qaMatch) {
    // Log the query
    await logAIQuery({
      userId,
      userName,
      question,
      response: qaMatch.answer,
      context,
      matchedQAId: qaMatch.id,
    });
    return { answer: qaMatch.answer, source: 'qa', matchedQAId: qaMatch.id };
  }

  // 2. Get AI settings
  const settings = await getAISettings();
  if (!settings?.apiKey) {
    // Log the query even without API key
    const fallback = "I'm sorry, I can't answer that right now. The AI service hasn't been configured yet. Please ask an admin to add an API key in Settings > AI Configuration.";
    await logAIQuery({ userId, userName, question, response: fallback, context });
    return { answer: fallback, source: 'fallback' };
  }

  // 3. Build system prompt
  const defaultSupportPrompt = `You are a helpful support assistant for Breakin' Bread, a music platform. Help users with platform features, music production questions, and general inquiries. Be concise and friendly.`;
  const defaultCoachPrompt = `You are an AI Music Coach for Breakin' Bread. Help users with music theory, production techniques, mixing, mastering, songwriting, music business, and career advice. Be encouraging and educational.`;

  const systemPrompt = context === 'support'
    ? (settings.systemPrompt || defaultSupportPrompt)
    : (settings.coachSystemPrompt || defaultCoachPrompt);

  // 4. Build Q&A context from related entries
  let qaContext = '';
  try {
    const allQA = await getQAEntries();
    const relevant = allQA
      .filter(e => e.isActive)
      .slice(0, 10)
      .map(e => `Q: ${e.question}\nA: ${e.answer}`)
      .join('\n\n');
    if (relevant) qaContext = relevant;
  } catch { /* ignore */ }

  // 5. Call AI API
  try {
    const answer = await callAIAPI(settings, systemPrompt, question, qaContext);
    await logAIQuery({ userId, userName, question, response: answer, context });
    return { answer, source: 'ai' };
  } catch (error: any) {
    console.error('AI API error:', error);
    const fallback = `I encountered an error processing your request: ${error.message}. Please try again or contact support.`;
    await logAIQuery({ userId, userName, question, response: fallback, context });
    return { answer: fallback, source: 'fallback' };
  }
}
